import { GeoPermissionState } from '../types';

export interface NormalizedLocationError {
  code: GeolocationPositionError['code'] | 'UNSUPPORTED' | 'UNKNOWN';
  message: string;
}

const supportsGeolocation = () => typeof navigator !== 'undefined' && !!navigator.geolocation;


export const FAST_ACQUISITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 30000,
  timeout: 12000,
};

export const REFINED_TRACKING_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 1000,
  timeout: 10000,
};

export const normalizeGeolocationError = (err: GeolocationPositionError | null): NormalizedLocationError => {
  if (!err) return { code: 'UNKNOWN', message: 'Location unavailable' };
  if (err.code === 1) return { code: err.code, message: 'Permission denied' };
  if (err.code === 2) return { code: err.code, message: 'Position unavailable' };
  if (err.code === 3) return { code: err.code, message: 'Location request timed out' };
  return { code: 'UNKNOWN', message: err.message || 'Unknown geolocation error' };
};

export const requestGeolocationPermission = async (): Promise<GeoPermissionState> => {
  if (!supportsGeolocation()) return 'unsupported';

  if (navigator.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      if (status.state === 'granted') return 'granted';
      if (status.state === 'denied') return 'denied';
      return 'prompt';
    } catch {
      // fall through to conservative fallback when Permissions API errors
    }
  }

  return 'prompt';
};

export const getCurrentPositionOnce = (options?: PositionOptions): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!supportsGeolocation()) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { ...REFINED_TRACKING_OPTIONS, ...options }
    );
  });
};

export const startLocationWatch = (
  onSuccess: (pos: GeolocationPosition) => void,
  onError: (err: NormalizedLocationError) => void,
  options?: PositionOptions
): number | null => {
  if (!supportsGeolocation()) {
    onError({ code: 'UNSUPPORTED', message: 'Geolocation not supported' });
    return null;
  }

  try { return navigator.geolocation.watchPosition(
    (pos) => onSuccess(pos),
    (err) => onError(normalizeGeolocationError(err)),
    { ...REFINED_TRACKING_OPTIONS, ...options }
  ); } catch {
    onError({ code: 'UNSUPPORTED', message: 'Location requires HTTPS (or localhost) and browser location access.' });
    return null;
  }
};

export const stopLocationWatch = (watchId: number | null) => {
  if (watchId !== null && supportsGeolocation()) {
    navigator.geolocation.clearWatch(watchId);
  }
};
